import { RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface RetryAction {
  id: string;
  action_type: string;
  params: Record<string, unknown>;
  error_message?: string;
}

interface RetryConfirmationProps {
  retryAction: RetryAction;
  onRetry: (action: RetryAction) => void;
  onDismiss: (actionId: string) => void;
  isLoading?: boolean;
}

export function RetryConfirmation({ retryAction, onRetry, onDismiss, isLoading }: RetryConfirmationProps) {
  const getActionLabel = (actionType: string): string => {
    switch (actionType) {
      case 'add_supplier_from_marketplace':
        return 'Add Supplier';
      case 'request_new_supplier':
        return 'Request Supplier';
      case 'update_supplier':
        return 'Update Supplier';
      case 'create_poam':
        return 'Create Action';
      case 'assign_poam':
        return 'Assign Action';
      default:
        return 'Retry Action';
    }
  };

  return (
    <div className="mt-3 p-4 rounded-xl border border-destructive/30 bg-destructive/5 space-y-3">
      <div className="flex items-start gap-2">
        <div className="shrink-0 h-6 w-6 rounded-full bg-destructive/10 flex items-center justify-center">
          <RefreshCw className="h-3.5 w-3.5 text-destructive" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-destructive">Action Failed</p>
          {retryAction.error_message && (
            <p className="text-xs text-muted-foreground mt-1 break-words">
              {retryAction.error_message}
            </p>
          )}
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onRetry(retryAction)}
          disabled={isLoading}
          className={cn(
            "flex-1 gap-2 border-primary/30 hover:bg-primary/10",
            isLoading && "opacity-50"
          )}
        >
          <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
          {isLoading ? 'Retrying...' : `Retry: ${getActionLabel(retryAction.action_type)}`}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDismiss(retryAction.id)}
          disabled={isLoading}
          className="shrink-0"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export type { RetryAction };
